/**
 * RK debug logging utility.
 *
 * Levels (highest → lowest verbosity):
 *   verbose  — every render, effect entry, state read, branch taken
 *   info     — flow milestones (redirect decisions, fetch results, storage ops)
 *   warn     — unexpected-but-recoverable states
 *   error    — always logged regardless of debug level
 *
 * Enable from the browser console (survives page refresh):
 *   localStorage.setItem('rk_debug', 'verbose')   // everything
 *   localStorage.setItem('rk_debug', 'info')       // milestones only
 *   localStorage.setItem('rk_debug', 'warn')       // warnings + errors
 *   localStorage.removeItem('rk_debug')            // off (errors still show)
 *
 * Enable for a single page load via query param (no localStorage write):
 *   https://responsekit.net/login?debug=verbose
 *
 * DEV default: on dev.responsekit.net (and any other host getEnvironment()
 * classifies as 'DEV' — localhost, dev.*, *.dev.*), debug logging defaults to
 * 'info' with NO opt-in required. This is a precedence FALLBACK ONLY — an
 * explicit query param or localStorage value (e.g. rk_debug=warn) always wins
 * over the DEV default. PROD/UAT are unaffected: still silent unless a user
 * explicitly opts in via query param or localStorage, exactly as before.
 *
 * Output format:
 *   [RK:Component] +123ms  message  {data?}
 *   timestamp is ms since page load for tight sequencing
 */

import { getEnvironment } from './environment';

type DebugLevel = 'verbose' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<DebugLevel, number> = {
  verbose: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/** DEV-only fallback level, used only when no explicit query param / localStorage override is set. */
const DEV_DEFAULT_LEVEL: DebugLevel = 'info';

function getConfiguredLevel(): DebugLevel | null {
  // Query param takes precedence (one-time, no storage write)
  try {
    const qp = new URLSearchParams(window.location.search).get('debug');
    if (qp && qp in LEVEL_ORDER) return qp as DebugLevel;
  } catch { /* non-browser env */ }

  try {
    const stored = localStorage.getItem('rk_debug');
    if (stored && stored in LEVEL_ORDER) return stored as DebugLevel;
  } catch { /* storage unavailable */ }

  // No explicit user choice — fall back to a DEV-only default so debug logs
  // are visible out of the box in dev without requiring manual opt-in.
  // PROD/UAT remain silent by default (unchanged behavior).
  try {
    if (getEnvironment() === 'DEV') return DEV_DEFAULT_LEVEL;
  } catch { /* non-browser env */ }

  return null;
}

function shouldLog(level: DebugLevel): boolean {
  if (level === 'error') return true; // always
  const configured = getConfiguredLevel();
  if (!configured) return false;
  return LEVEL_ORDER[level] >= LEVEL_ORDER[configured];
}

const pageStart = typeof performance !== 'undefined' ? performance.now() : 0;

function timestamp(): string {
  const elapsed = typeof performance !== 'undefined'
    ? Math.round(performance.now() - pageStart)
    : 0;
  return `+${elapsed}ms`;
}

function formatPrefix(component: string, level: DebugLevel): string {
  const levelTag = level === 'error' ? '🔴' : level === 'warn' ? '🟡' : level === 'info' ? '🔵' : '⚪';
  return `${levelTag} [RK:${component}] ${timestamp()}`;
}

/** Log at verbose level — every render, effect, branch, storage read. */
export function dbgV(component: string, message: string, data?: unknown): void {
  if (!shouldLog('verbose')) return;
  data !== undefined
    ? console.debug(formatPrefix(component, 'verbose'), message, data)
    : console.debug(formatPrefix(component, 'verbose'), message);
}

/** Log at info level — flow milestones: redirects, fetches, auth decisions. */
export function dbgI(component: string, message: string, data?: unknown): void {
  if (!shouldLog('info')) return;
  data !== undefined
    ? console.info(formatPrefix(component, 'info'), message, data)
    : console.info(formatPrefix(component, 'info'), message);
}

/** Log at warn level — unexpected-but-recoverable states. */
export function dbgW(component: string, message: string, data?: unknown): void {
  if (!shouldLog('warn')) return;
  data !== undefined
    ? console.warn(formatPrefix(component, 'warn'), message, data)
    : console.warn(formatPrefix(component, 'warn'), message);
}

/** Log at error level — always logged, regardless of debug setting. */
export function dbgE(component: string, message: string, data?: unknown): void {
  data !== undefined
    ? console.error(formatPrefix(component, 'error'), message, data)
    : console.error(formatPrefix(component, 'error'), message);
}

/**
 * Convenience: log a timing measurement.
 * Usage:
 *   const t = dbgTimer('AuthCallback');
 *   // ... do work ...
 *   t('code exchange complete');
 */
export function dbgTimer(component: string): (label: string) => void {
  const start = typeof performance !== 'undefined' ? performance.now() : 0;
  return (label: string) => {
    if (!shouldLog('info')) return;
    const elapsed = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : 0) - start
    );
    console.info(formatPrefix(component, 'info'), `${label} (${elapsed}ms since timer start)`);
  };
}

/**
 * Convenience: group related debug calls (verbose only).
 * Wraps console.group/groupEnd so the browser collapses them.
 */
export function dbgGroup(component: string, label: string, fn: () => void): void {
  if (!shouldLog('verbose')) { fn(); return; }
  console.groupCollapsed(formatPrefix(component, 'verbose'), label);
  try { fn(); } finally { console.groupEnd(); }
}

/** Return current debug level, or null if disabled. For components that want to gate expensive serialization. */
export function debugLevel(): DebugLevel | null {
  return getConfiguredLevel();
}

function wallClock(): string {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

/**
 * Always-on debug log — replaces raw `console.log('[RK-DEBUG] ...')` calls.
 * Adds a wall-clock timestamp so sequential events can be correlated across
 * browser console screenshots.
 */
export function rkLog(...args: unknown[]): void {
  console.log(`[RK-DEBUG] ${wallClock()}`, ...args);
}
